"use client";

import React from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import { useEvent, useWebinarScheduleLoading } from "@/contexts/EventContext";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";

interface LiveWebinarProps {
  pageName: string;
  title: string;
  pageType?: string;
  darkBackground?: boolean;
}

const Live_webinar: React.FC<LiveWebinarProps> = ({
  pageName,
  title,
  pageType,
  darkBackground,
}) => {
  const pathname = usePathname();
  const latestEvent = useEvent();
  const time = latestEvent?.time ? latestEvent?.time : "4:00 PM";
  const isScheduleLoading = useWebinarScheduleLoading();
  const courseId = pageName === "CPA" ? 1 : pageName === "CPA/CMA" ? 1 : 2;

  // Hide images on specific URL
  const shouldHideImages =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  // Hide form heading on specific URLs
  const shouldHideFormHeading =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india" ||
    pathname === "/best-us-cma-coaching-in-india";

  // Hide mobile form image on specific URLs
  const shouldHideMobileFormImage =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india" ||
    pathname === "/best-us-cma-coaching-in-india";

  return (
    <section
      className={`w-full pt-10 ${
        darkBackground ? "bg-[#061A2F]" : "bg-[#FFFFFF]"
      }`}
    >
      <div className="container mx-auto px-4">
        <div
          className={`grid grid-cols-1 md:gap-0 gap-6 items-center ${shouldHideImages ? "md:grid-cols-2 md:gap-[35%] md:justify-between " : "md:grid-cols-2 lg:grid-cols-3"}`}
        >
          <div>
            {pageType === "caira" ? (
              <h2
                className={`text-2xl md:text-[72px] font-bold md:leading-[1] -tracking-normal text-center md:text-left leading-[1.16] ${
                  darkBackground ? "text-white" : "text-black"
                }`}
              >
                Talk to a CAIRA Advisor
              </h2>
            ) : (
              <>
                <h2
                  className={`text-2xl md:text-[40px] font-bold md:leading-[50px] -tracking-normal text-center md:text-left ${
                    darkBackground ? "text-white" : "text-black"
                  }`}
                >
                  Live Webinar: <span className="md:block">{title}</span>
                </h2>
                <div
                  className={
                    shouldHideImages
                      ? "flex flex-wrap items-center justify-center md:block gap-1 md:gap-0"
                      : ""
                  }
                >
                  <h3
                    className={`md:text-[32px] md:mt-2 ${shouldHideImages ? "text-base" : "text-base"} font-bold ${shouldHideImages ? "" : "text-center md:text-left"} ${
                      darkBackground ? "text-white" : "text-black"
                    }`}
                  >
                    By Varun Jain
                  </h3>
                  <p
                    className={`bg-[#39F] md:mt-6 bg-clip-text text-transparent ${shouldHideImages ? "text-base" : "text-base"} md:text-[28px] font-work-sans font-bold ${shouldHideImages ? "" : "text-center md:text-left"}`}
                  >
                    Every Saturday at{" "}
                    {isScheduleLoading ? (
                      // Parent text is transparent (bg-clip-text), so give the
                      // skeleton a visible colour to tint from.
                      <span className="text-[#39F]">
                        <WebinarDateSkeleton width={70} />
                      </span>
                    ) : (
                      <>{`${time}`}</>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Desktop Image - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
          {!shouldHideImages && (
            <div className="hidden md:flex justify-center">
              <Image
                src={
                  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/girl-at-right-point.webp"
                }
                alt="Join Us"
                width={300}
                height={420}
                className="w-full max-w-[294px] h-auto object-contain"
              />
            </div>
          )}

          <div
            className={`relative w-full ${pageType === "caira" ? "mt-0" : shouldHideMobileFormImage ? "mt-0" : "mt-20 md:mt-0"}`}
          >
            {pageType === "caira" ? (
              <div className="w-full max-w-sm md:max-w-md mx-auto">
                <div className="rounded-[16px] md:rounded-[24px] border-[1px] border-[#606e96] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_54.9px_rgba(166,137,255,0.30)] backdrop-blur-[6px] p-4 md:p-6">
                  <GlobalForm
                    course={pageName}
                    courseId={courseId}
                    formType="page"
                    isAStepperForm={true}
                    pageType="accounting page"
                    formHeading={FormHeading.GET_YOUR_CAIRA_ROADMAP}
                    subCategory={
                      pageName === "CPA"
                        ? FinanceAndAccountingSubcategory.CPA
                        : FinanceAndAccountingSubcategory.CMA
                    }
                    submitButtonName="Request a Call Back"
                    successMessage={SuccessMessageType.ACCOUNTING}
                    verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                    showSVG={false}
                    darkBackground={true}
                  />
                </div>
              </div>
            ) : pageType === "caira-auditosrs" ? (
              <div className="w-full max-w-sm md:max-w-md mx-auto">
                <div className="rounded-[16px] md:rounded-[24px] border-[1px] border-[#606e96] bg-[rgba(33,100,167,0.10)] shadow-[inset_0_0_54.9px_rgba(166,137,255,0.30)] backdrop-blur-[6px] p-4 md:p-6">
                  <GlobalForm
                    course={pageName}
                    courseId={courseId}
                    formType="page"
                    isAStepperForm={true}
                    pageType="accounting page"
                    formHeading={FormHeading.TALK_TO_CAIRA_AUDITORS}
                    subCategory={
                      pageName === "CPA"
                        ? FinanceAndAccountingSubcategory.CPA
                        : FinanceAndAccountingSubcategory.CMA
                    }
                    submitButtonName="Request a Call Back"
                    successMessage={SuccessMessageType.ACCOUNTING}
                    verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                    showSVG={false}
                    darkBackground={true}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Mobile Image - Hidden on specific URLs */}
                {!shouldHideMobileFormImage && (
                  <div className="md:hidden">
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/formImageMobile.webp?tr=w-255,h-190,q-80"
                      alt="boy"
                      width={220}
                      height={166}
                      sizes="255px"
                      className="ml-1 z-[1] absolute top-[-90px] w-[220px] h-[166px] object-contain"
                      loading="lazy"
                    />
                  </div>
                )}

                <div
                  id="live-webinar-form"
                  className={`w-full max-w-sm md:pt-6 md:mb-0 mb-6 p-4 border border-white rounded-2xl bg-[#fff]/10  backdrop-blur-[100px] shadow-[0px_0px_14px_rgba(4,84,175,0.29)] ${shouldHideFormHeading ? "pt-4" : "pt-16"}`}
                >
                  {!shouldHideFormHeading && (
                    <h3
                      className={`text-lg font-semibold  text-center mb-4 ${darkBackground ? "text-white" : "text-[#061A2F]"}`}
                    >
                      Register for the AI in Accounting 101 Webinar
                    </h3>
                  )}
                  <GlobalForm
                    course={pageName}
                    courseId={courseId}
                    formType="page"
                    isAStepperForm={true}
                    pageType="accounting page"
                    subCategory={
                      pageName === "CPA"
                        ? FinanceAndAccountingSubcategory.CPA
                        : FinanceAndAccountingSubcategory.CMA
                    }
                    submitButtonName={
                      shouldHideMobileFormImage
                        ? "Join FREE Webinar"
                        : "Request a Call Back"
                    }
                    successMessage={SuccessMessageType.ACCOUNTING}
                    verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                    showSVG={false}
                    darkBackground={darkBackground ? true : false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Live_webinar;
